/**
 * API client for the two-step OAuth login flow.
 *
 * Step 1 — discoverUnits:  POST /auth/login (var=2) to learn whether the
 *                          account is single-unit or multi-unit, and (when
 *                          multi-unit) which homes are available.
 * Step 2 — oauthLogin:     POST /oauth/login with the chosen `unit` (or none
 *                          for single-unit accounts) to obtain the OAuth
 *                          authorization code.
 *
 * Both endpoints are form-encoded (application/x-www-form-urlencoded) and
 * accept a sha1-hashed password — matches both legacy oauth_login.js and
 * shelly-app-3's standardLogin behavior.
 *
 * The auth/login response shape for multi-unit accounts is not exhaustively
 * documented in shelly_api/docs; parsing is intentionally tolerant of both
 * `data.availableUnits` and top-level `availableUnits`.
 */
import sha1 from 'crypto-js/sha1';
import { resolveBaseUrlForEmail } from '../lib/api-base';
import { errorCodeFromHttp, extractErrorCode, type ErrorCode } from '../lib/errors';

export type AvailableUnits = Record<string, string>;

export type DiscoverResult =
	| { kind: 'single' }
	| { kind: 'multi'; availableUnits: AvailableUnits; defaultUnitId?: number }
	| { kind: 'error'; code: ErrorCode };

export type OAuthLoginResult = { kind: 'ok'; code: string } | { kind: 'error'; code: ErrorCode };

export interface OAuthLoginInput {
	email: string;
	password: string;
	clientId: string;
	responseType?: string;
	unit?: number | string;
}

export interface AuthClientDeps {
	baseUrl?: (email: string) => string;
	fetchImpl?: typeof fetch;
}

const defaultDeps: Required<AuthClientDeps> = {
	baseUrl: (email) => resolveBaseUrlForEmail(email),
	fetchImpl: (...args) => fetch(...args),
};

/**
 * Step 1 — call /auth/login with var=2 to discover units.
 */
export async function discoverUnits(
	email: string,
	password: string,
	deps: AuthClientDeps = {},
): Promise<DiscoverResult> {
	const { baseUrl, fetchImpl } = { ...defaultDeps, ...deps };
	const normalizedEmail = email.toLowerCase();
	const body = formEncode({
		email: normalizedEmail,
		password: sha1(password).toString(),
		var: '2',
	});

	let res: Response;
	try {
		res = await fetchImpl(`${baseUrl(normalizedEmail)}/auth/login`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body,
		});
	} catch {
		return { kind: 'error', code: errorCodeFromHttp(undefined, 'network') };
	}

	const json = await safeJson(res);
	if (!res.ok || (json && (json as { isok?: boolean }).isok === false)) {
		const code = json ? extractErrorCode(json) : errorCodeFromHttp(res.status, 'response');
		return { kind: 'error', code };
	}

	const parsed = parseDiscoverPayload(json);
	return parsed;
}

/**
 * Step 2 — call /oauth/login to issue the authorization code.
 * Pass `unit` for multi-unit accounts; omit for single-unit (server defaults
 * to the first unit in pkey.id).
 */
export async function oauthLogin(
	input: OAuthLoginInput,
	deps: AuthClientDeps = {},
): Promise<OAuthLoginResult> {
	const { baseUrl, fetchImpl } = { ...defaultDeps, ...deps };
	const normalizedEmail = input.email.toLowerCase();

	const fields: Record<string, string> = {
		email: normalizedEmail,
		password: sha1(input.password).toString(),
		client_id: input.clientId,
	};
	if (input.responseType) fields.response_type = input.responseType;
	if (input.unit !== undefined && input.unit !== '') fields.unit = String(input.unit);

	let res: Response;
	try {
		res = await fetchImpl(`${baseUrl(normalizedEmail)}/oauth/login`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: formEncode(fields),
		});
	} catch {
		return { kind: 'error', code: errorCodeFromHttp(undefined, 'network') };
	}

	const json = await safeJson(res);
	if (!res.ok || (json && (json as { isok?: boolean }).isok === false)) {
		const code = json ? extractErrorCode(json) : errorCodeFromHttp(res.status, 'response');
		return { kind: 'error', code };
	}

	const code = readOAuthCode(json);
	if (!code) return { kind: 'error', code: 'generic_error' };
	return { kind: 'ok', code };
}

/**
 * Parse the auth/login response into a discriminated DiscoverResult.
 *
 * Server contract (see shelly_api/controllers/auth.php::login_action):
 *   - Multi-unit:  `{ isok: true, data: { availableUnits: { id: name, ... } } }`
 *                  — no `multiple_units` flag in the response payload; the
 *                    presence of `data.availableUnits` IS the signal.
 *   - Single-unit: `{ isok: true, data: { token, lang, user_api_url, ... } }`
 *                  — no `availableUnits` key at all.
 *
 * The parser also accepts a top-level `availableUnits` and an optional
 * `default_unit_id` (not currently emitted by the server, kept for forward
 * compatibility with documented multi-unit endpoints).
 */
function parseDiscoverPayload(json: unknown): DiscoverResult {
	const root = json as Record<string, unknown> | null;
	if (!root) return { kind: 'error', code: 'generic_error' };

	const data = (root.data as Record<string, unknown> | undefined) ?? {};
	const availableUnitsRaw =
		(data.availableUnits as unknown) ?? (root.availableUnits as unknown);
	const defaultUnitIdRaw =
		(data.default_unit_id as unknown) ?? (root.default_unit_id as unknown);

	if (
		availableUnitsRaw &&
		typeof availableUnitsRaw === 'object' &&
		Object.keys(availableUnitsRaw as Record<string, unknown>).length > 0
	) {
		return {
			kind: 'multi',
			availableUnits: availableUnitsRaw as AvailableUnits,
			defaultUnitId:
				typeof defaultUnitIdRaw === 'number' ? defaultUnitIdRaw : undefined,
		};
	}

	return { kind: 'single' };
}

function readOAuthCode(json: unknown): string | null {
	const root = json as Record<string, unknown> | null;
	if (!root) return null;
	const data = root.data as Record<string, unknown> | undefined;
	const code = (data?.code ?? root.code) as unknown;
	return typeof code === 'string' && code.length > 0 ? code : null;
}

function formEncode(fields: Record<string, string>): string {
	const params = new URLSearchParams();
	for (const [k, v] of Object.entries(fields)) params.append(k, v);
	return params.toString();
}

async function safeJson(res: Response): Promise<unknown> {
	try {
		return await res.json();
	} catch {
		return null;
	}
}
